import { cn } from "@/lib/utils"
import { Link } from "@inertiajs/react"
import { IconChevronDown, IconChevronRight, IconFileText } from "@tabler/icons-react"
import { useState } from "react"
import type { DocumentSection, TreeNode } from "../types"

const SECTION_LABELS: Record<DocumentSection, string> = {
  company: "Company",
  shared: "Shared with me",
  private: "Private",
}

const SECTION_ORDER: DocumentSection[] = ["company", "shared", "private"]

function childrenOf(nodes: TreeNode[], parentId: number | null) {
  return nodes.filter((node) => node.parent_id === parentId)
}

function subtreeIds(node: TreeNode, nodes: TreeNode[]): number[] {
  return [node.id, ...childrenOf(nodes, node.id).flatMap((child) => subtreeIds(child, nodes))]
}

interface TreeItemProps {
  node: TreeNode
  nodes: TreeNode[]
  activeId?: number
  depth: number
}

function TreeItem({ node, nodes, activeId, depth }: TreeItemProps) {
  const children = childrenOf(nodes, node.id)
  const [open, setOpen] = useState(() => subtreeIds(node, nodes).includes(activeId ?? -1))
  const isActive = node.id === activeId

  return (
    <li>
      <div
        className={cn(
          "flex items-center gap-1 rounded-md pr-2 text-sm hover:bg-accent",
          isActive && "bg-accent font-medium",
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {children.length > 0 ? (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-label={`${open ? "Collapse" : "Expand"} ${node.title}`}
            className="flex size-5 shrink-0 items-center justify-center rounded hover:bg-muted"
          >
            {open ? <IconChevronDown className="size-3.5" /> : <IconChevronRight className="size-3.5" />}
          </button>
        ) : (
          <span className="flex size-5 shrink-0 items-center justify-center">
            <IconFileText className="size-3.5 text-muted-foreground" />
          </span>
        )}

        <Link href={`/documents/${node.id}`} className="flex-1 truncate py-1.5">
          {node.title}
        </Link>
      </div>

      {open && children.length > 0 && (
        <ul>
          {children.map((child) => (
            <TreeItem key={child.id} node={child} nodes={nodes} activeId={activeId} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  )
}

interface DocumentTreeProps {
  nodes: TreeNode[]
  activeId?: number
}

export function DocumentTree({ nodes, activeId }: DocumentTreeProps) {
  const roots = childrenOf(nodes, null)

  if (roots.length === 0) {
    return <p className="px-3 py-2 text-sm text-muted-foreground">No pages yet.</p>
  }

  return (
    <nav aria-label="Pages" className="space-y-4">
      {SECTION_ORDER.map((section) => {
        const sectionRoots = roots.filter((node) => node.section === section)
        if (sectionRoots.length === 0) return null

        return (
          <div key={section}>
            <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {SECTION_LABELS[section]}
            </p>
            <ul>
              {sectionRoots.map((node) => (
                <TreeItem key={node.id} node={node} nodes={nodes} activeId={activeId} depth={0} />
              ))}
            </ul>
          </div>
        )
      })}
    </nav>
  )
}
